/**
 * Promo countdown bar.
 *
 * Rewritten for main-thread cost. The previous version called setInterval(1s)
 * and on every tick re-assigned textContent on all four timer fields, inside an
 * aria-live="polite" region, regardless of whether the value had changed. Only
 * the seconds field changes on 59 of every 60 ticks, yet every write invalidated
 * style for the whole bar, forcing a style recalc each time (measured ~2,090
 * recalcStyleCount per 20s on the live homepage).
 *
 * What changed:
 *   - Writes are memoized per field: a tick that changes nothing writes nothing.
 *   - Ticks are scheduled with setTimeout aligned to the next second boundary,
 *     so the displayed second flips with the clock instead of drifting, and the
 *     cadence backs off when the deadline is far away.
 *   - Ticking stops when the tab is hidden or the bar is scrolled out of view,
 *     and while the deadline has already passed (only re-checked occasionally so
 *     the next cycle is still picked up).
 *   - Rotation height-locking is skipped while the tab is hidden.
 *
 * The section-unload cleanup and the rotation behaviour are preserved.
 */
(function () {
  const SECOND = 1000;
  const MINUTE = 60 * SECOND;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;

  // Re-check cadences. The timer is only worth a per-second tick near the end.
  const FAR_CHECK = 10 * SECOND;
  const NEAR_THRESHOLD = 3 * MINUTE;
  const EXPIRED_CHECK = 30 * SECOND;
  const VISIBILITY_MARGIN = "200px";
  // Land each tick just past a second boundary. Targeting the boundary exactly
  // let a tick fire a millisecond before it, so the following tick landed in the
  // same displayed second and its write was correctly skipped as a no-op - which
  // made the seconds digit occasionally sit for two seconds and then jump by two.
  const BOUNDARY_EPSILON_MS = 25;

  function parseOffsetHours(rawOffset) {
    const parsed = Number(rawOffset);
    return Number.isFinite(parsed) ? parsed : 10;
  }

  function getTimezoneDate(offsetHours) {
    const now = new Date();
    const utcMillis = now.getTime() + now.getTimezoneOffset() * MINUTE;
    return new Date(utcMillis + offsetHours * HOUR);
  }

  /**
   * Parse the cycle start time.
   *
   * This deliberately does NOT clamp hours to 23. The live block passes "48:00",
   * whose hour component is a day offset rather than a wall-clock hour, and
   * clamping it to 23 turned the anchor into 23:00. The hour is reduced modulo 24
   * at the point of use instead, which keeps the anchor phase (48 % 24 === 0) and
   * lets the same parser serve any configuration.
   */
  function parseHourMinute(value) {
    const match = String(value || "").trim().match(/^(\d{1,3}):(\d{2})$/);
    if (!match) {
      return { hours: 0, minutes: 0 };
    }

    const hours = Math.max(0, Number(match[1]));
    const minutes = Math.min(59, Math.max(0, Number(match[2])));
    return { hours, minutes };
  }

  function toTwoDigits(value) {
    return String(Math.max(0, value)).padStart(2, "0");
  }

  function getTargetTime(bar) {
    const mode = bar.dataset.timerMode || "24";
    const offsetHours = parseOffsetHours(bar.dataset.timezoneOffset);
    const nowInZone = getTimezoneDate(offsetHours);

    if (mode === "fixed") {
      const endDateRaw = bar.dataset.endDate;
      if (!endDateRaw) return null;
      const parsedDate = new Date(endDateRaw);
      return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.getTime();
    }

    if (mode === "48") {
      const { hours, minutes } = parseHourMinute(bar.dataset.resetStartTime);
      const cycle = 48 * HOUR;

      // A true 48-hour cycle. The anchor is a FIXED instant — 2026-01-01 00:00 in
      // the configured timezone, shifted by the configured start time — not
      // "today's" midnight. Anchoring on today would only ever measure a few
      // hours of elapsed time, so the counter would read 47h right after a reset
      // and then snap back down at every midnight instead of every second day.
      // A fixed anchor gives one stable phase: reset every 48h, same local time.
      //
      // The hour is still reduced modulo 24 because the legacy config passes
      // "48:00", where the hour component encodes a day offset rather than a
      // wall-clock hour.
      const anchorWithinDay = (hours % 24) * HOUR + minutes * MINUTE;
      const anchor =
        Date.UTC(2026, 0, 1, 0, 0, 0) - offsetHours * HOUR + anchorWithinDay;

      const elapsed = Date.now() - anchor;
      const cycleElapsed = ((elapsed % cycle) + cycle) % cycle;
      return Date.now() + (cycle - cycleElapsed);
    }

    // Default: rolling 24h reset at local midnight in configured timezone.
    const midnight = new Date(nowInZone);
    midnight.setHours(24, 0, 0, 0);
    return midnight.getTime();
  }

  function formatParts(totalMs, showDays) {
    const safeMs = Math.max(0, totalMs);
    const days = Math.floor(safeMs / DAY);
    const remainderAfterDays = safeMs % DAY;
    const hours = Math.floor(remainderAfterDays / HOUR);
    const remainderAfterHours = remainderAfterDays % HOUR;
    const minutes = Math.floor(remainderAfterHours / MINUTE);
    const seconds = Math.floor((remainderAfterHours % MINUTE) / SECOND);

    return {
      days,
      displayDays: showDays && days > 0,
      hours: toTwoDigits(hours),
      minutes: toTwoDigits(minutes),
      seconds: toTwoDigits(seconds),
    };
  }

  function setField(el, value, lastKey, state) {
    if (!el || state[lastKey] === value) return;
    el.textContent = value;
    state[lastKey] = value;
  }

  /**
   * Render the current remaining time.
   *
   * Returns the deadline so the caller can schedule the next tick, or null when
   * the bar is misconfigured (no usable deadline).
   */
  function render(state) {
    const bar = state.bar;
    const targetTime = getTargetTime(bar);
    if (targetTime == null) return null;

    const parts = formatParts(targetTime - Date.now(), bar.dataset.showDays === "true");

    setField(state.daysEl, String(parts.days), "lastDays", state);
    setField(state.hoursEl, parts.hours, "lastHours", state);
    setField(state.minutesEl, parts.minutes, "lastMinutes", state);
    setField(state.secondsEl, parts.seconds, "lastSeconds", state);

    if (state.lastDisplayDays !== parts.displayDays) {
      if (state.daysContainer) {
        state.daysContainer.style.display = parts.displayDays ? "" : "none";
      }
      if (state.daysSeparator) {
        state.daysSeparator.style.display = parts.displayDays ? "" : "none";
      }
      state.lastDisplayDays = parts.displayDays;
    }

    return targetTime;
  }

  function nextDelay(state, targetTime) {
    if (targetTime == null) return FAR_CHECK;

    const remaining = targetTime - Date.now();
    // Deadline already passed: stop per-second ticking while the next cycle is
    // computed, but keep an occasional look in case the target rolls over.
    if (remaining <= 0) return EXPIRED_CHECK;

    const alignedToSecond = SECOND - (Date.now() % SECOND) + BOUNDARY_EPSILON_MS;
    // When a seconds field is on screen it has to advance once per second, or
    // the digit visibly jumps in steps. Ticking that often is cheap now that a
    // tick which changes nothing writes nothing; the old cost was the writing,
    // not the timer.
    if (state.showsSeconds) return alignedToSecond;
    if (remaining > NEAR_THRESHOLD) return FAR_CHECK;
    return alignedToSecond;
  }

  function tick(state) {
    state.timer = null;
    const targetTime = render(state);
    if (state.paused) return;
    state.timer = setTimeout(function () {
      tick(state);
    }, nextDelay(state, targetTime));
  }

  function start(state) {
    if (state.timer != null || state.paused) return;
    state.timer = setTimeout(function () {
      tick(state);
    }, nextDelay(state, getTargetTime(state.bar)));
  }

  function stop(state) {
    if (state.timer != null) {
      clearTimeout(state.timer);
      state.timer = null;
    }
  }

  function setPaused(state, paused) {
    if (state.paused === paused) return;
    state.paused = paused;
    if (paused) {
      stop(state);
      stopRotation(state);
    } else {
      start(state);
      scheduleRotation(state);
    }
  }

  function lockBarHeight(state) {
    const bar = state.bar;
    // Lock height to the taller slide so rotation cannot shrink the bar
    // (height flicker was bumping scrollY and auto-hiding the sticky header).
    // Skipped while the tab is hidden: the reflow it forces is pointless then.
    if (document.hidden) return;

    const primary = state.primary;
    const secondary = state.secondary;
    const prevPrimaryHidden = primary.hidden;
    const prevSecondaryHidden = secondary.hidden;

    primary.hidden = false;
    secondary.hidden = true;
    const primaryHeight = bar.offsetHeight;

    primary.hidden = true;
    secondary.hidden = false;
    const secondaryHeight = bar.offsetHeight;

    primary.hidden = prevPrimaryHidden;
    secondary.hidden = prevSecondaryHidden;

    const locked = Math.max(primaryHeight, secondaryHeight, bar.offsetHeight);
    if (locked > 0) {
      bar.style.minHeight = locked + "px";
    }
    bar.style.overflowAnchor = "none";
  }

  function setupRotation(state) {
    const bar = state.bar;
    if (bar.dataset.rotate !== "true") return;

    const primary = bar.querySelector('[data-rotate-slide="primary"]');
    const secondary = bar.querySelector('[data-rotate-slide="secondary"]');
    if (!primary || !secondary) return;

    state.primary = primary;
    state.secondary = secondary;
    state.showingPrimary = true;

    const primaryDuration = parseDurationSeconds(bar.dataset.rotatePrimaryDuration, 3) * SECOND;
    const secondaryDuration = parseDurationSeconds(bar.dataset.rotateSecondaryDuration, 5) * SECOND;
    state.primaryDuration = primaryDuration;
    state.secondaryDuration = secondaryDuration;

    lockBarHeight(state);
    showSlide(state, true);
    scheduleRotation(state);
  }

  function parseDurationSeconds(rawValue, fallback) {
    const parsed = Number(rawValue);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  }

  function showSlide(state, showPrimary) {
    const { primary, secondary } = state;
    state.showingPrimary = showPrimary;
    primary.hidden = !showPrimary;
    secondary.hidden = showPrimary;
    // Retrigger the fade-in animation on the visible slide.
    const visible = showPrimary ? primary : secondary;
    visible.style.animation = "none";
    void visible.offsetWidth;
    visible.style.animation = "";
  }

  function scheduleRotation(state) {
    // Rotation is optional and off on most surfaces. Guard on it actually being
    // configured, otherwise this armed a 0ms timeout and threw on the first
    // slide swap. Un-pausing calls this, so the guard belongs here rather than
    // only at the call sites.
    if (!isRotating(state)) return;
    if (state.paused || state.rotateTimer != null) return;
    state.rotateTimer = setTimeout(function () {
      state.rotateTimer = null;
      if (state.paused) return;
      showSlide(state, !state.showingPrimary);
      scheduleRotation(state);
    }, state.showingPrimary ? state.primaryDuration : state.secondaryDuration);
  }

  function stopRotation(state) {
    if (state.rotateTimer != null) {
      clearTimeout(state.rotateTimer);
      state.rotateTimer = null;
    }
  }

  function isRotating(state) {
    return state.primary != null && state.secondary != null;
  }

  function createState(bar) {
    const secondsEl = bar.querySelector("[data-seconds]");
    return {
      bar,
      daysEl: bar.querySelector("[data-days]"),
      hoursEl: bar.querySelector("[data-hours]"),
      minutesEl: bar.querySelector("[data-minutes]"),
      secondsEl,
      daysContainer: bar.querySelector("[data-days-container]"),
      daysSeparator: bar.querySelector("[data-days-separator]"),
      // A visible seconds field forces a once-per-second cadence.
      showsSeconds: Boolean(secondsEl),
      lastDays: null,
      lastHours: null,
      lastMinutes: null,
      lastSeconds: null,
      lastDisplayDays: null,
      timer: null,
      rotateTimer: null,
      observer: null,
      visible: true,
      // Start paused so the first setPaused(state, false) is a real transition
      // and actually starts the timer. Starting unpaused made that call a no-op
      // and the countdown never ticked at all.
      paused: true,
    };
  }

  // Track live bars so the tab-visibility handler can reach them. A WeakMap
  // would be tidier but is not iterable, and this list is small.
  const activeStates = [];

  function initialize() {
    document.querySelectorAll("[data-promo-countdown-bar]").forEach(function (bar) {
      if (!bar || bar.dataset.promoCountdownReady === "true") return;
      bar.dataset.promoCountdownReady = "true";

      const state = createState(bar);
      render(state);
      setupRotation(state);

      if (typeof IntersectionObserver === "function") {
        state.observer = new IntersectionObserver(
          function (entries) {
            const entry = entries[entries.length - 1];
            state.visible = entry.isIntersecting;
            setPaused(state, !state.visible || document.hidden);
          },
          { rootMargin: VISIBILITY_MARGIN },
        );
        state.observer.observe(bar);
      }

      activeStates.push(state);
      setPaused(state, document.hidden || !state.visible);
    });
  }

  function onSectionUnload(event) {
    const section = event && event.target;
    if (!section) return;
    section.querySelectorAll("[data-promo-countdown-bar]").forEach(function (bar) {
      for (let i = activeStates.length - 1; i >= 0; i--) {
        const state = activeStates[i];
        if (state.bar !== bar) continue;
        stop(state);
        stopRotation(state);
        if (state.observer) {
          state.observer.disconnect();
          state.observer = null;
        }
        activeStates.splice(i, 1);
      }
      bar.dataset.promoCountdownReady = "false";
    });
  }

  document.addEventListener("visibilitychange", function () {
    for (const state of activeStates) {
      setPaused(state, document.hidden || !state.visible);
    }
  });

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initialize);
  } else {
    initialize();
  }

  document.addEventListener("shopify:section:load", initialize);
  document.addEventListener("shopify:section:unload", onSectionUnload);
})();
